/**
 * Browser-safe date parsing.
 *
 * Problem: `new Date("2026-03-12")` is parsed as UTC midnight per ECMAScript spec.
 * In timezones like CET (UTC+1), `toLocaleDateString()` then shows the PREVIOUS day
 * because UTC 00:00 = CET 23:00 on March 11th. Edge follows the spec strictly,
 * Chrome is sometimes lenient.
 *
 * Fix: Date-only strings ("YYYY-MM-DD") are parsed as local dates.
 * Datetime strings with time/timezone info are parsed normally.
 */

/** Parse a date string safely as local date (not UTC) */
export function parseLocalDate(dateStr: string | null | undefined): Date {
  if (!dateStr) return new Date(NaN);
  // Date-only: "2026-03-12" → parse as local
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  // Everything else (datetime, ISO with T, etc.) → default parsing
  return new Date(dateStr);
}

/** Format a date string as "DD.MM.YYYY" (German locale), safe for all browsers */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const d = parseLocalDate(dateStr);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Format a date string as "DD.MM.YYYY, HH:MM:SS" (German locale), safe for all browsers */
export function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const d = parseLocalDate(dateStr);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('de-DE');
}
